import {
  Bar,
  BarChart,
  Label,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import {
  BusinessTrendChartData,
  TimeFrame,
} from "../../services/interfaces/IBusinessCostTrend";
import {
  dateFormatterToMMDD,
  dateFormatterToMMMYY,
  numberFormatter,
} from "../../utils/formatter";

interface IBusinessCostBarChartProps {
  data: BusinessTrendChartData[];
  timeFrame: TimeFrame;
  commodity: string | null;
}

const BusinessCostBarChart = ({
  data,
  timeFrame,
  commodity,
}: IBusinessCostBarChartProps) => {
  const values = data.flatMap((d) => [d.volume]);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  const lowerBound = minValue * 0.9;
  const upperBound = maxValue * 1.1;

  const DataFormatter = (value: number) => {
    return Math.round(value / 1000).toString();
  };

  const BusinessCostTrendTooltip: React.FC<TooltipProps<string, number>> = ({
    active,
    payload,
    label,
  }) => {
    const formatLbsValue = (value: any) => {
      const numericValue = Number(value);
      if (value !== null && value !== "" && !Number.isNaN(numericValue)) {
        return `${numericValue.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })} lbs`;
      }
      return value;
    };

    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "#fff",
            padding: "10px",
            border: "1px solid #bebebe",
          }}
        >
          <p>
            Date:{" "}
            {timeFrame === TimeFrame.MONTH
              ? dateFormatterToMMMYY(label)
              : dateFormatterToMMDD(label)}
          </p>
          {payload?.map((item: any) => (
            <p
              key={item.dataKey}
              style={{ color: item.color, textTransform: "capitalize" }}
            >
              {item?.name?.toString().replace("_", " ")} :{" "}
              {formatLbsValue(item.value)}
            </p>
          ))}
        </div>
      );
    }

    return null;
  };

  const CustomYAxisLabel = ({ viewBox, value }: any) => {
    const { x, y, width, height } = viewBox;
    const lines = value.split("\n");
    const cx = x;
    const cy = height / 2;
    return (
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        transform={`rotate(-90, ${cx}, ${cy})`}
        fill="black"
        fontSize={"0.6vw"}
        fontWeight={600}
      >
        {lines.map((line: any, index: number) => (
          <tspan
            fill={index === 0 ? "#86BC25" : "black"}
            key={index}
            x={index === 2 ? 0.4 * viewBox.x : viewBox.x}
            dy={index === 2 ? `1.1em` : `${index * 1.3}em`}
          >
            {line}
          </tspan>
        ))}
      </text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        margin={{
          top: 2,
          right: 10,
          // bottom: 2,
          left: 20,
        }}
      >
        <XAxis
          dataKey="date"
          tickFormatter={(dateStr) =>
            timeFrame === TimeFrame.MONTH
              ? dateFormatterToMMMYY(dateStr)
              : dateFormatterToMMDD(dateStr)
          }
        />
        <YAxis
          dataKey="volume"
          tickLine={true}
          tickFormatter={DataFormatter}
          /* domain={[Math.floor(lowerBound), Math.ceil(upperBound)]} */
          interval={"preserveStartEnd"}
          // domain={([dataMin, dataMax]) => {
          //   const divFactor = dataMax / 1000 > 100 ? 40 : (dataMax / 1000 > 1 ? 20 : 0.2);
          //   console.log(dataMin / 1000 + "_" + dataMax / 1000 + "_" + divFactor);
          //   return [Math.floor((dataMin / 1000) / divFactor) * divFactor, Math.ceil(dataMax / 1000 * divFactor) * (divFactor)];
          // }}
          domain={([dataMin, dataMax]) => {
            // Round down the maximum to the nearest 50k for better viz
            const maxRounded = Math.ceil(dataMax / 50000) * 50000; // Round up to nearest 50k
            if (timeFrame === TimeFrame.WEEK) {
              let maxRounded = Math.ceil(dataMax / 1000) * 1000;
              return [0, maxRounded];
            }
            return [0, maxRounded];
          }}
          tickCount={3}
        >
          <Label
            className="text-fs12"
            style={{
              textAnchor: "middle",
              fill: "black",
            }}
            angle={-90}
            value={commodity + "\n" + "Sales Volume" + "\n" + "(K lbs)"}
            position={"insideLeft"}
            content={<CustomYAxisLabel />}
          />
        </YAxis>
        <Bar dataKey="volume" fill="#b4d4e4" />
        <Tooltip content={<BusinessCostTrendTooltip />} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default BusinessCostBarChart;
